// src/pages/ReviewManagement.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Rating,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../utils/api';

interface Review {
  id: number;
  rating: number;
  text: string;
  status: string;
  createdAt: string;
  user?: {
    name: string;
  };
  provider?: {
    businessName: string;
  };
}

const ReviewManagement: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const { user } = useAuth();

  const fetchReviews = useCallback(async () => {
    try {
      const response = await apiRequest('/admin/reviews');
      if (!response.ok) throw new Error('Failed to fetch reviews');
      const data = await response.json();
      setReviews(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleApprove = async (reviewId: number) => {
    try {
      const response = await apiRequest(`/admin/reviews/${reviewId}/approve`, {
        method: 'PUT',
      });
      
      if (!response.ok) throw new Error('Failed to approve review');
      
      setMessage({ type: 'success', text: 'Review approved successfully!' });
      fetchReviews();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to approve review' });
    }
  };

  const handleReject = async (reviewId: number) => {
    try {
      const response = await apiRequest(`/admin/reviews/${reviewId}/reject`, {
        method: 'PUT',
      });
      
      if (!response.ok) throw new Error('Failed to reject review');
      
      setMessage({ type: 'success', text: 'Review rejected successfully!' });
      fetchReviews();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to reject review' });
    }
  };

  if (user?.role !== 'admin') {
    return (
      <Container>
        <Typography variant="h4" color="error">
          Access Denied. Admin privileges required.
        </Typography>
      </Container>
    );
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Review Management
      </Typography>
      
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Provider</TableCell>
              <TableCell>Reviewer</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Review</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reviews.map((review) => (
              <TableRow key={review.id}>
                <TableCell>{review.provider?.businessName || 'Unknown'}</TableCell>
                <TableCell>{review.user?.name || 'Anonymous'}</TableCell>
                <TableCell>
                  <Rating value={review.rating} readOnly size="small" />
                </TableCell>
                <TableCell>{review.text}</TableCell>
                <TableCell>
                  <Chip 
                    label={review.status} 
                    color={review.status === 'approved' ? 'success' : review.status === 'pending' ? 'warning' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{new Date(review.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  {review.status === 'pending' && (
                    <Box>
                      <Button 
                        size="small" 
                        color="success" 
                        onClick={() => handleApprove(review.id)}
                        sx={{ mr: 1 }}
                      >
                        Approve
                      </Button>
                      <Button 
                        size="small" 
                        color="error" 
                        onClick={() => handleReject(review.id)}
                      >
                        Reject
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default ReviewManagement;